import { useMemo, useRef, useState } from 'react';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  LockClosedIcon,
  LockOpen1Icon,
} from '@radix-ui/react-icons';
import { Checkbox } from '@radix-ui/themes';
import { TrashIcon } from '@heroicons/react/24/solid';
import { XIcon } from '@phosphor-icons/react';
import { Toolpad } from '../Toolpad';
import { useUIStore, useMovieStore, useSelectionStore } from '../../../stores';
import { useSignalR } from '../../../hooks';
import { useTranslation } from '../../../i18n';
import type { SceneObject, SceneObjectResponse } from '../../../types';

type ObjectTreeNode = {
  object: SceneObject;
  children: ObjectTreeNode[];
};

function buildObjectTree(objects: SceneObject[]): ObjectTreeNode[] {
  const byParent = new Map<string | null, SceneObject[]>();

  for (const object of objects) {
    const parentKey = object.parentObjectId ?? null;
    const siblings = byParent.get(parentKey);
    if (siblings) {
      siblings.push(object);
    } else {
      byParent.set(parentKey, [object]);
    }
  }

  const sortNodes = (list: SceneObject[]) =>
    [...list].sort((a, b) => b.zIndex - a.zIndex || a.name.localeCompare(b.name));

  const build = (parentId: string | null): ObjectTreeNode[] => {
    const children = sortNodes(byParent.get(parentId) ?? []);
    return children.map((object) => ({
      object,
      children: build(object.id),
    }));
  };

  const roots = build(null);

  // Orphan parent ids (missing parent) — show as roots so nothing disappears
  const knownIds = new Set(objects.map((o) => o.id));
  const orphanParents = [...byParent.keys()].filter(
    (key): key is string => key != null && !knownIds.has(key),
  );
  for (const orphanParent of orphanParents) {
    roots.push(...build(orphanParent));
  }

  return roots;
}

function collectDescendantIds(node: ObjectTreeNode): string[] {
  const ids: string[] = [];
  for (const child of node.children) {
    ids.push(child.object.id, ...collectDescendantIds(child));
  }
  return ids;
}

function collectSubtreeIds(node: ObjectTreeNode): string[] {
  return [node.object.id, ...collectDescendantIds(node)];
}

function getTypeIcon(object: SceneObject): string {
  if (object.type === 'group' || object.isGroup) return '▦';
  if (object.type === 'image') return '📷';
  if (object.type === 'text') return 'T';
  if (object.type === 'shape') return '□';
  if (object.type === 'clip') return '🎬';
  return '•';
}

export function SceneObjectList() {
  const { t } = useTranslation();
  const { toolpads, toggleToolpad } = useUIStore();
  const { currentMovie, currentScene, deleteSceneObject, updateSceneObject } = useMovieStore();
  const {
    status: signalRStatus,
    connect,
    sceneObjectDelete,
    sceneObjectUpdateLabel,
    sceneObjectSetVisibility,
    sceneObjectSetLock,
  } = useSignalR();
  const isOpen = toolpads.sceneObjectList.isOpen;
  const [editingObjectId, setEditingObjectId] = useState<string | null>(null);
  /** Gruplar varsayılan kapalı; sadece kullanıcının açtıkları burada tutulur. */
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  /** İptal / Escape ile kapanırken blur'un kaydetmesini engellemek için */
  const skipBlurRenameRef = useRef(false);
  const { selectedIds, select, toggleSelection, selectAll, clearSelection, multiSelect } =
    useSelectionStore();

  const objects = currentScene?.objects ?? [];
  const tree = useMemo(() => buildObjectTree(objects), [objects]);
  const allIds = useMemo(() => objects.map((o) => o.id), [objects]);

  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleRename = async (objectId: string, newName: string) => {
    const objectInScene = currentScene?.objects.find((o) => o.id === objectId);
    if (!objectInScene) return;

    const trimmed = newName.trim();
    const finalName = trimmed || objectInScene.name;
    if (finalName === objectInScene.name) return;

    const parsedId = Number(objectId);
    if (!Number.isFinite(parsedId)) {
      console.warn('[SceneObjectList] Invalid object id for rename:', objectId);
      return;
    }

    try {
      if (signalRStatus !== 'Connected') {
        await connect();
      }
      const saved = await sceneObjectUpdateLabel({ id: parsedId, name: finalName, description: '' });
      updateSceneObject(objectId, { name: saved.name });
    } catch (error) {
      console.error('[SceneObjectList] Failed to rename scene object on server:', error);
      updateSceneObject(objectId, { name: objectInScene.name });
    }
  };

  const handleDelete = async (objectId: string) => {
    if (!currentMovie || !currentScene) return;

    const objectInScene = currentScene.objects.find((obj) => obj.id === objectId);
    if (!objectInScene) return;

    const parsedId = Number(objectId);
    if (!Number.isFinite(parsedId)) {
      console.warn('[SceneObjectList] Invalid object id for delete:', objectId);
      return;
    }

    const payload: SceneObjectResponse = {
      id: parsedId,
      movieId: Number(currentMovie.id),
      sceneId: Number(currentScene.id),
      layerId: objectInScene.layerId ?? 0,
      libraryObjectId: objectInScene.libraryObjectId ?? null,
      name: objectInScene.name,
      description: '',
      type: objectInScene.type,
      body: '',
      fileName: undefined,
      fullPath: objectInScene.src ?? '',
      isVisible: objectInScene.visible,
      isLocked: objectInScene.locked,
      sorter: objectInScene.zIndex,
      width: objectInScene.width,
      height: objectInScene.height,
      dropPosition: { top: objectInScene.y, left: objectInScene.x },
      defaultState: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      if (signalRStatus !== 'Connected') {
        await connect();
      }
      await sceneObjectDelete(payload);
      deleteSceneObject(objectId);
    } catch (error) {
      console.error('[SceneObjectList] Failed to delete scene object from backend:', error);
    }
  };

  const handleToggleVisibility = async (objectId: string) => {
    const objectInScene = currentScene?.objects.find((o) => o.id === objectId);
    if (!objectInScene) return;

    const parsedId = Number(objectId);
    if (!Number.isFinite(parsedId)) {
      console.warn('[SceneObjectList] Invalid object id for visibility:', objectId);
      return;
    }

    const nextVisible = !objectInScene.visible;
    updateSceneObject(objectId, { visible: nextVisible });

    try {
      if (signalRStatus !== 'Connected') {
        await connect();
      }
      const saved = await sceneObjectSetVisibility({ id: parsedId, isVisible: nextVisible });
      updateSceneObject(objectId, { visible: saved.isVisible !== false });
    } catch (error) {
      console.error('[SceneObjectList] Failed to set visibility:', error);
      updateSceneObject(objectId, { visible: objectInScene.visible });
    }
  };

  const handleToggleLock = async (objectId: string) => {
    const objectInScene = currentScene?.objects.find((o) => o.id === objectId);
    if (!objectInScene) return;

    const parsedId = Number(objectId);
    if (!Number.isFinite(parsedId)) {
      console.warn('[SceneObjectList] Invalid object id for lock:', objectId);
      return;
    }

    const nextLocked = !objectInScene.locked;
    updateSceneObject(objectId, { locked: nextLocked });

    try {
      if (signalRStatus !== 'Connected') {
        await connect();
      }
      const saved = await sceneObjectSetLock({ id: parsedId, isLocked: nextLocked });
      updateSceneObject(objectId, { locked: saved.isLocked === true });
    } catch (error) {
      console.error('[SceneObjectList] Failed to set lock:', error);
      updateSceneObject(objectId, { locked: objectInScene.locked });
    }
  };

  const toggleCollapsed = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSubtreeSelection = (node: ObjectTreeNode) => {
    const subtreeIds = collectSubtreeIds(node);
    const allInSubtreeSelected = subtreeIds.every((id) => selectedIds.includes(id));
    if (allInSubtreeSelected) {
      multiSelect(selectedIds.filter((id) => !subtreeIds.includes(id)));
    } else {
      const merged = new Set([...selectedIds, ...subtreeIds]);
      multiSelect([...merged]);
    }
  };

  const renderNode = (node: ObjectTreeNode, depth: number) => {
    const { object, children } = node;
    const hasChildren = children.length > 0;
    const isCollapsed = !expandedIds.has(object.id);
    const isSelected = selectedIds.includes(object.id);
    const subtreeIds = hasChildren ? collectSubtreeIds(node) : [object.id];
    const selectedInSubtree = subtreeIds.filter((id) => selectedIds.includes(id)).length;
    const subtreeAllSelected = selectedInSubtree === subtreeIds.length;
    const subtreeSomeSelected = selectedInSubtree > 0 && !subtreeAllSelected;

    return (
      <div key={object.id} className="object-tree-node">
        <div
          className={[
            'object-list-item flex',
            isSelected ? 'selected' : '',
            !object.visible ? 'is-hidden' : '',
            object.locked ? 'is-locked' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ paddingLeft: 8 + depth * 14 }}
          onClick={() => select(object.id)}
          onDoubleClick={() => setEditingObjectId(object.id)}
        >
          {hasChildren ? (
            <button
              type="button"
              className="object-tree-toggle"
              aria-label={
                isCollapsed
                  ? t('toolpads.sceneObjects.expand')
                  : t('toolpads.sceneObjects.collapse')
              }
              onClick={(e) => {
                e.stopPropagation();
                toggleCollapsed(object.id);
              }}
            >
              {isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </button>
          ) : (
            <span className="object-tree-toggle-spacer" aria-hidden="true" />
          )}

          <Checkbox
            size="1"
            checked={hasChildren ? (subtreeSomeSelected ? 'indeterminate' : subtreeAllSelected) : isSelected}
            aria-label={
              hasChildren
                ? subtreeAllSelected
                  ? t('toolpads.sceneObjects.deselectObjectAndChildren', { name: object.name })
                  : t('toolpads.sceneObjects.selectObjectAndChildren', { name: object.name })
                : t('toolpads.sceneObjects.selectObject', { name: object.name })
            }
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={() => {
              if (hasChildren) {
                toggleSubtreeSelection(node);
              } else {
                toggleSelection(object.id);
              }
            }}
          />

          <span className="object-type-icon">{getTypeIcon(object)}</span>

          {editingObjectId === object.id ? (
            <div className="object-editing-container flex items-center gap-2">
              <input
                type="text"
                className="object-name-input w-24 flex-1"
                defaultValue={object.name}
                autoFocus
                aria-label={t('toolpads.sceneObjects.objectNameAria')}
                onBlur={(e) => {
                  if (skipBlurRenameRef.current) {
                    skipBlurRenameRef.current = false;
                    return;
                  }
                  void handleRename(object.id, e.target.value);
                  setEditingObjectId(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    skipBlurRenameRef.current = true;
                    void handleRename(object.id, e.currentTarget.value);
                    setEditingObjectId(null);
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    skipBlurRenameRef.current = true;
                    setEditingObjectId(null);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="button"
                className="cursor-pointer"
                title={t('common.save')}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const input = e.currentTarget.parentElement?.querySelector<HTMLInputElement>(
                    '.object-name-input',
                  );
                  skipBlurRenameRef.current = true;
                  void handleRename(object.id, input?.value ?? '');
                  setEditingObjectId(null);
                }}
              >
                <CheckIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="cursor-pointer"
                title={t('common.cancel')}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  skipBlurRenameRef.current = true;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingObjectId(null);
                }}
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="object-name-container flex flex-1 items-center gap-2 min-w-0">
              <span className="object-name">{object.name}</span>
              {hasChildren && (
                <button
                  type="button"
                  className="object-select-children"
                  title={t('toolpads.sceneObjects.selectAllChildren')}
                  onClick={(e) => {
                    e.stopPropagation();
                    const childIds = collectDescendantIds(node);
                    const allChildrenSelected = childIds.every((id) => selectedIds.includes(id));
                    if (allChildrenSelected) {
                      multiSelect(selectedIds.filter((id) => !childIds.includes(id)));
                    } else {
                      multiSelect([...new Set([...selectedIds, ...childIds])]);
                    }
                  }}
                >
                  {t('toolpads.sceneObjects.all')}
                </button>
              )}
              <span className="object-actions">
                <button
                  type="button"
                  className={`cursor-pointer ${object.visible ? 'is-object-visible' : 'is-object-hidden'}`}
                  title={object.visible ? t('common.hide') : t('common.show')}
                  aria-label={object.visible ? t('common.hide') : t('common.show')}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleToggleVisibility(object.id);
                  }}
                >
                  {object.visible ? (
                    <EyeOpenIcon className="w-3.5 h-3.5" />
                  ) : (
                    <EyeClosedIcon className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  className={`cursor-pointer ${object.locked ? 'is-object-locked' : ''}`}
                  title={object.locked ? t('common.unlock') : t('common.lock')}
                  aria-label={object.locked ? t('common.unlock') : t('common.lock')}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleToggleLock(object.id);
                  }}
                >
                  {object.locked ? (
                    <LockClosedIcon className="w-3.5 h-3.5" />
                  ) : (
                    <LockOpen1Icon className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  className="cursor-pointer object-delete"
                  title={t('common.delete')}
                  aria-label={t('common.delete')}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDelete(object.id);
                  }}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </span>
            </div>
          )}
        </div>

        {hasChildren && !isCollapsed && (
          <div className="object-tree-children">
            {children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Toolpad
      title={t('toolpads.sceneObjects.title')}
      isOpen={isOpen}
      onToggle={() => toggleToolpad('sceneObjectList')}
    >
      <div className="object-list">
        {objects.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-3 gap-2 h-full">
            <p className="text-xs text-theme-muted text-center">
              {t('toolpads.sceneObjects.empty')}
            </p>
          </div>
        ) : (
          <>
            <div className="object-list-toolbar">
              <label className="object-list-select-all">
                <Checkbox
                  size="1"
                  checked={someSelected ? 'indeterminate' : allSelected}
                  onCheckedChange={() => {
                    if (allSelected) clearSelection();
                    else selectAll(allIds);
                  }}
                  aria-label={t('toolpads.sceneObjects.selectAllAria')}
                />
                <span>{t('toolpads.sceneObjects.selectAll')}</span>
              </label>
              {selectedIds.length > 0 && (
                <span className="object-list-selection-count">
                  {t('toolpads.sceneObjects.selectedCount', { count: selectedIds.length })}
                </span>
              )}
            </div>
            {tree.map((node) => renderNode(node, 0))}
          </>
        )}
      </div>
    </Toolpad>
  );
}
